import { Link } from 'react-router-dom';
import { UserPlus, FileBadge, KeyRound, Pencil, ArrowUpRight, Car } from 'lucide-react';
import { DATE_COLS, STATE_STYLE, STATUS_BADGE, dateState, fmtDate, daysText } from './utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Ring = ({ pct }) => {
  const r = 16;
  const c = 2 * Math.PI * r;
  const color = pct === 100 ? '#10b981' : pct >= 60 ? '#f59e0b' : '#ef4444';
  return (
    <svg width="42" height="42" viewBox="0 0 42 42" className="shrink-0" aria-label={`%${pct}`}>
      <circle cx="21" cy="21" r={r} fill="none" stroke="currentColor" className="text-slate-200 dark:text-slate-700" strokeWidth="4" />
      <circle cx="21" cy="21" r={r} fill="none" stroke={color} strokeWidth="4" strokeLinecap="round"
        strokeDasharray={`${(pct / 100) * c} ${c}`} transform="rotate(-90 21 21)" />
      <text x="21" y="25" textAnchor="middle" className="fill-slate-800 dark:fill-slate-100" fontSize="11" fontWeight="700">{pct}</text>
    </svg>
  );
};

const Plate = ({ plate }) => (
  <span className="inline-flex items-stretch overflow-hidden rounded-md border-2 border-red-600 bg-white shadow-sm">
    <span className="bg-blue-700 text-[8px] font-bold text-yellow-300 px-1 flex items-end pb-0.5">B</span>
    <span className="px-2 py-0.5 font-mono text-[15px] font-black tracking-wider text-red-600">{plate}</span>
  </span>
);

const DatePill = ({ label, iso }) => {
  const { state, days } = dateState(iso);
  return (
    <div className={`rounded-lg border px-2 py-1 text-[11px] leading-tight ${STATE_STYLE[state]}`}>
      <div className="opacity-70">{label}</div>
      <div className="font-semibold">{iso ? fmtDate(iso) : 'yok'}</div>
      {iso && state !== 'ok' && <div className="font-bold">{daysText(days)}</div>}
    </div>
  );
};

export function VehicleCard({ r, brandIcons, onAssign, onVergunning, onEdit }) {
  const st = STATUS_BADGE[r.status] || STATUS_BADGE.ACTIVE;
  const rental = r.ownership_type === 'Kiralık';
  const brandKey = (r.brand || '').trim().toUpperCase();
  const critical = r.warnings.some((w) => w.level === 'critical');
  const missing = [...r.missing_items.filter((m) => m !== 'Şoför' && m !== 'Vergunning'), ...r.missing_fields];
  const accent = critical ? 'before:bg-red-500' : r.warnings.length ? 'before:bg-orange-400' : r.status !== 'ACTIVE' ? 'before:bg-violet-500' : 'before:bg-emerald-500';

  return (
    <article data-testid={`vehicle-card-${r.id}`}
      className={`group relative overflow-hidden rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 pl-5 shadow-sm transition-[box-shadow,transform] duration-200 hover:-translate-y-0.5 hover:shadow-lg before:absolute before:inset-y-0 before:left-0 before:w-1.5 ${accent}`}>
      <header className="flex items-start gap-3">
        <div className="h-11 w-11 shrink-0 rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center overflow-hidden">
          {brandIcons.has(brandKey)
            ? <img src={`${API_URL}/api/brand-icons/${encodeURIComponent(brandKey)}/image`} alt={r.brand} className="h-8 w-8 object-contain" />
            : <Car className="h-5 w-5 text-slate-400" />}
        </div>
        <div className="min-w-0 flex-1">
          <Plate plate={r.license_plate} />
          <p className="mt-1 text-xs text-slate-500 truncate">{[r.brand, r.model].filter(Boolean).join(' ') || 'Marka/model yok'}{r.firm_name ? ` · ${r.firm_name}` : ''}</p>
        </div>
        <Ring pct={r.completion} />
      </header>

      <div className="mt-3 flex flex-wrap gap-1">
        <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${st.cls}`}>{st.label}</span>
        {r.ownership_type && <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${rental ? 'bg-sky-100 text-sky-700 border-sky-300' : 'bg-slate-100 text-slate-600 border-slate-200'}`}>{r.ownership_type}</span>}
        {r.warnings.length > 0 && <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold text-white ${critical ? 'bg-red-600' : 'bg-orange-500'}`}>{r.warnings.length} uyarı</span>}
      </div>

      {rental && (
        <div className="mt-2 rounded-lg bg-sky-50 dark:bg-sky-950/30 px-2.5 py-1.5 text-[11px] text-sky-800 dark:text-sky-200 flex items-center gap-1.5">
          <KeyRound className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate">{r.replaces_plate ? <><b>{r.replaces_plate}</b> yerine · </> : ''}{r.rental_company || 'firma?'} · dönüş <b>{r.rental_end_date ? fmtDate(r.rental_end_date) : '?'}</b></span>
        </div>
      )}

      <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
        <button type="button" onClick={() => onAssign(r)} disabled={r.status !== 'ACTIVE' && !r.current_drivers.length} data-testid={`vehicle-card-assign-${r.id}`}
          className={`rounded-lg border px-2 py-1.5 text-left transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${r.current_drivers.length ? 'border-slate-200 hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800' : 'border-dashed border-sky-400 text-sky-700 hover:bg-sky-50'}`}>
          <div className="text-[10px] uppercase text-slate-400">Şoför</div>
          <div className="font-semibold truncate flex items-center gap-1">
            {r.current_drivers.length ? r.current_drivers.map((d) => d.full_name).join(', ') : <><UserPlus className="h-3.5 w-3.5" />Ata</>}
          </div>
        </button>
        <button type="button" onClick={() => onVergunning(r)} data-testid={`vehicle-card-vergunning-${r.id}`}
          className={`rounded-lg border px-2 py-1.5 text-left transition-colors ${r.vergunning_codes.length ? 'border-slate-200 hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800' : 'border-dashed border-indigo-300 text-indigo-600 hover:bg-indigo-50'}`}>
          <div className="text-[10px] uppercase text-slate-400">Vergunning</div>
          <div className="font-semibold truncate flex items-center gap-1">
            {r.vergunning_codes.length ? r.vergunning_codes.join(', ') : <><FileBadge className="h-3.5 w-3.5" />Ata</>}
          </div>
        </button>
      </div>

      <div className="mt-2 grid grid-cols-3 gap-1.5">
        {DATE_COLS.map((c) => <DatePill key={c.key} label={c.label} iso={r[c.key]} />)}
      </div>

      <footer className="mt-3 flex items-center gap-2">
        <div className="flex-1 min-w-0 flex flex-wrap gap-1">
          {missing.slice(0, 3).map((m) => <span key={m} className="rounded bg-amber-50 border border-amber-200 text-amber-700 px-1.5 py-0.5 text-[10px]">{m}</span>)}
          {missing.length > 3 && <span className="text-[10px] text-slate-400">+{missing.length - 3}</span>}
          {missing.length === 0 && <span className="text-[11px] text-emerald-600 font-semibold">Bilgiler tam</span>}
        </div>
        <button type="button" onClick={() => onEdit(r)} className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-800 dark:hover:bg-slate-800" title="Hızlı düzenle" data-testid={`vehicle-card-edit-${r.id}`}>
          <Pencil className="h-4 w-4" />
        </button>
        <Link to={`/dashboard/vehicles/${r.id}`} className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-sky-700 dark:hover:bg-slate-800" title="Araç ayrıntısı" data-testid={`vehicle-card-open-${r.id}`}>
          <ArrowUpRight className="h-4 w-4" />
        </Link>
      </footer>
    </article>
  );
}
